/**
 * useTagData Hook - Centralized Tag Data Fetching with Caching
 * 
 * Mirrors the useAuthorData pattern for consistency.
 * Provides centralized tag data management with:
 * - Global in-memory cache
 * - Request deduplication
 * - Batch fetching support
 * - Cache invalidation
 * 
 * @example
 * const { getTag, prefetchTags, loading } = useTagData();
 * 
 * // Get a single tag (cached)
 * const tag = getTag('blockchain');
 * 
 * // Prefetch multiple tags
 * await prefetchTags(['blockchain', 'defi', 'nft']);
 */

import { useState, useCallback } from 'react';
import { tagService } from '../lib/tag-service';
import { Tag } from '../types/tags';
import { createVersionedLRUCache, LRUCache } from '@/utils/lru-cache';

// Global LRU cache with automatic eviction
const CACHE_VERSION = '1.1.0'; // Upgraded to LRU cache
const MAX_CACHE_SIZE = 300; // Maximum number of tags to cache
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

let globalTagCache: LRUCache<Tag | null>;

// Pending requests to avoid duplicate fetches
const pendingRequests = new Map<string, Promise<Tag | null>>();

let cacheInitialized = false;

interface TagDataState {
  [tagSlug: string]: Tag | null;
}

export interface UseTagDataReturn {
  getTag: (tagSlug: string) => Tag | null;
  prefetchTags: (tagSlugs: string[]) => Promise<void>;
  invalidateTag: (tagSlug: string) => void;
  invalidateAllTags: () => void;
  loading: boolean;
  getCacheStats: () => ReturnType<LRUCache<Tag | null>['getStats']>;
  isTagCached: (tagSlug: string) => boolean;
}

function initializeCache() {
  if (cacheInitialized) return;

  globalTagCache = createVersionedLRUCache<Tag | null>(
    'useTagData',
    CACHE_VERSION,
    {
      maxSize: MAX_CACHE_SIZE,
      ttl: CACHE_TTL,
      onEvict: (key, value) => {
        if (process.env.NODE_ENV === 'development') {

        }
      }
    }
  );

  // Periodic cleanup of expired entries (every 5 minutes)
  if (typeof window !== 'undefined') {
    setInterval(() => {
      const cleaned = globalTagCache.cleanup();
      if (cleaned > 0 && process.env.NODE_ENV === 'development') {

      }
    }, 5 * 60 * 1000);
  }

  cacheInitialized = true;
}

export function useTagData(): UseTagDataReturn {
  const [tagData, setTagData] = useState<TagDataState>({});
  const [loading, setLoading] = useState(false);

  // Initialize LRU cache on first use
  if (typeof window !== 'undefined' && !cacheInitialized) {
    initializeCache();
  }

  const fetchTag = useCallback(async (tagSlug: string): Promise<Tag | null> => {
    // Ensure cache is initialized
    if (!cacheInitialized) {
      initializeCache();
    }

    // Check LRU cache first (includes TTL check)
    const cached = globalTagCache.get(tagSlug);
    if (cached !== null) {
      return cached;
    }

    // Check if request is already pending
    if (pendingRequests.has(tagSlug)) {
      return pendingRequests.get(tagSlug)!;
    }

    // Create new request
    const request = (async () => {
      try {
        // Use getTagBySlugOrHandle to support both tag slugs and Twitter handles
        // This allows @mentions in AI-generated content to resolve to tags
        const tag = await tagService.getTagBySlugOrHandle(tagSlug);
        if (process.env.NODE_ENV === 'development') {

        }
        // LRU cache automatically handles eviction if full
        globalTagCache.set(tagSlug, tag);
        return tag;
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error(`[useTagData] Error fetching tag ${tagSlug}:`, error);
        }
        // Cache null to avoid repeated requests for non-existent tags
        globalTagCache.set(tagSlug, null);
        return null;
      }
    })();

    pendingRequests.set(tagSlug, request);

    try {
      const result = await request;
      return result;
    } finally {
      pendingRequests.delete(tagSlug);
    }
  }, []);

  const getTag = useCallback((tagSlug: string): Tag | null => {
    // Ensure cache is initialized
    if (!cacheInitialized) {
      initializeCache();
    }

    // Check LRU cache first (automatically updates access order)
    const cachedTag = globalTagCache.get(tagSlug);
    if (cachedTag !== null) {
      return cachedTag;
    }

    // If not cached, trigger fetch in background (don't update state during render)
    // The fetch will update the global cache, and subsequent calls will get the value
    if (!pendingRequests.has(tagSlug)) {
      fetchTag(tagSlug).catch(() => {
        // Error already logged in fetchTag
      });
    }

    return null;
  }, [fetchTag]);

  const prefetchTags = useCallback(async (tagSlugs: string[]) => {
    // Ensure cache is initialized
    if (!cacheInitialized) {
      initializeCache();
    }

    // Filter out slugs that are already in cache
    const slugsToFetch = tagSlugs.filter(slug => !globalTagCache.has(slug));

    if (slugsToFetch.length === 0) return;

    setLoading(true);

    try {
      // For each slug, either join the pending request or start a new one
      const promises = slugsToFetch.map(slug => {
        if (pendingRequests.has(slug)) {
          return pendingRequests.get(slug)!;
        }
        return fetchTag(slug);
      });

      const results = await Promise.all(promises);

      const newTagData: TagDataState = {};
      slugsToFetch.forEach((slug, index) => {
        newTagData[slug] = results[index];
      });

      setTagData(prev => ({ ...prev, ...newTagData }));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[useTagData] Error prefetching tags:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchTag]);

  const invalidateTag = useCallback((tagSlug: string) => {
    // Remove from global cache
    globalTagCache.delete(tagSlug);
    // Remove from local state
    setTagData(prev => {
      const newData = { ...prev };
      delete newData[tagSlug];
      return newData;
    });
  }, []);

  const invalidateAllTags = useCallback(() => {
    // Ensure cache is initialized
    if (!cacheInitialized) {
      initializeCache();
    }
    // Clear LRU cache
    globalTagCache.clear();
    // Clear local state
    setTagData({});
  }, []);

  const getCacheStats = useCallback(() => {
    // Ensure cache is initialized
    if (!cacheInitialized) {
      initializeCache();
    }
    return globalTagCache.getStats();
  }, []);

  const isTagCached = useCallback((tagSlug: string): boolean => {
    // Ensure cache is initialized
    if (!cacheInitialized) {
      initializeCache();
    }
    return globalTagCache.has(tagSlug);
  }, []);

  return {
    getTag,
    prefetchTags,
    invalidateTag,
    invalidateAllTags,
    loading,
    getCacheStats,
    isTagCached
  };
}

